import { applyEdit, type EditError } from "./edit";
import { editTokenIds } from "./lineage";
import type { Composition, CompositionEdit, CompositionToken } from "./schema";
import { alignmentKey } from "./tokenize";

export type CorrectionOutcome =
  | "resolved"
  | "saved-for-adult"
  | "deleted"
  | "open";

/** One surfaced word (or span), followed through every replacement of it. */
export interface WordFrustration {
  /** The id the word had when it was first surfaced. */
  readonly root: string;
  /** Child actions on the ladder: rejections, re-says, descriptions, choices, confirmations, saves. */
  readonly attempts: number;
  readonly failedAttempts: number;
  readonly reSays: number;
  readonly outcome: CorrectionOutcome;
  /** First surfacing to resolution or save; null while open or once deleted. */
  readonly msToResolution: number | null;
  /** Resolved, after a failure, to a word unlike anything heard before: the narrowing proxy. */
  readonly substituted: boolean;
}

export interface FrustrationMetrics {
  readonly surfacedWords: number;
  readonly words: readonly WordFrustration[];
  readonly meanAttempts: number | null;
  readonly medianMsToResolution: number | null;
  readonly savedForAdultShare: number | null;
  /** Delete edits that removed a word still open after a failed correction. */
  readonly deletionsAfterFailedCorrection: number;
  readonly substitutionsAfterFailedRecognition: number;
}

export type MetricsResult =
  | { readonly tag: "ok"; readonly value: FrustrationMetrics }
  | {
      readonly tag: "err";
      readonly error: { readonly editIndex: number; readonly cause: EditError };
    };

// A resolved word this far (normalized edit distance) from every earlier
// transcription of it reads as a different word, not a corrected spelling:
// "limousine" from "lemon zine" is 0.33, "car" from it is 1.
export const SUBSTITUTION_DISTANCE = 0.5;

export function editDistance(a: string, b: string): number {
  const row = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let diagonal = row[0] ?? 0;
    row[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const above = row[j] ?? 0;
      const left = row[j - 1] ?? 0;
      row[j] = Math.min(
        above + 1,
        left + 1,
        diagonal + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      diagonal = above;
    }
  }
  return row[b.length] ?? 0;
}

const normalizedDistance = (a: string, b: string): number => {
  const ka = alignmentKey(a);
  const kb = alignmentKey(b);
  const longest = Math.max(ka.length, kb.length);
  return longest === 0 ? 0 : editDistance(ka, kb) / longest;
};

interface Lineage {
  root: string;
  surfacedAt: number;
  attempts: number;
  failedAttempts: number;
  reSays: number;
  outcome: CorrectionOutcome;
  endedAt: number | null;
  pendingReplacementAt: number | null;
  heard: string[];
  finalText: string | null;
}

function resolvedText(edit: CompositionEdit): string | null {
  switch (edit.op) {
    case "confirm":
      return null;
    case "choose":
    case "type":
      return edit.to;
    case "describe":
      return edit.result.outcome === "chosen" ? edit.result.to : null;
    default:
      return null;
  }
}

const isResolving = (edit: CompositionEdit): boolean =>
  edit.op === "confirm" ||
  edit.op === "choose" ||
  edit.op === "type" ||
  (edit.op === "describe" && edit.result.outcome === "chosen");

const isFailure = (edit: CompositionEdit): boolean =>
  edit.op === "reject" ||
  (edit.op === "re-say" && edit.result.outcome !== "replaced") ||
  (edit.op === "describe" && edit.result.outcome !== "chosen");

const isChildAttempt = (edit: CompositionEdit): boolean =>
  edit.op === "reject" ||
  edit.op === "re-say" ||
  edit.op === "describe" ||
  edit.op === "save-for-adult" ||
  isResolving(edit);

const median = (values: readonly number[]): number | null => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  if (sorted.length === 0) return null;
  return sorted.length % 2 === 1
    ? (sorted[mid] ?? null)
    : ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
};

/**
 * The frustration measures the prototype is judged on, recomputed by
 * replaying the edit log from an empty composition, so they need nothing but
 * the log: attempts per surfaced word, time to resolution, the share saved
 * for a grown-up, deletions after a failed correction, and substitutions
 * after a failed recognition.
 */
export function frustrationMetrics(composition: Composition): MetricsResult {
  // Mutable bookkeeping is local to this replay and never escapes it.
  const rootOf = new Map<string, string>();
  const lineages = new Map<string, Lineage>();
  let deletionsAfterFailedCorrection = 0;

  const assignRoots = (tokens: readonly CompositionToken[]) => {
    for (const token of tokens) {
      if (rootOf.has(token.id)) continue;
      const ancestor =
        token.kind === "punct"
          ? undefined
          : token.supersedes.find((id) => rootOf.has(id));
      rootOf.set(token.id, rootOf.get(ancestor ?? "") ?? token.id);
    }
  };
  const textOf = (tokens: readonly CompositionToken[], root: string): string =>
    tokens
      .flatMap((t) =>
        t.kind === "word" && rootOf.get(t.id) === root ? [t.text] : [],
      )
      .join(" ");

  let current: Composition = { ...composition, tokens: [], edits: [] };
  for (const [editIndex, edit] of composition.edits.entries()) {
    const applied = applyEdit(current, edit);
    if (applied.tag === "err") {
      return { tag: "err", error: { editIndex, cause: applied.error } };
    }
    const before = current.tokens;
    current = applied.value;
    assignRoots(current.tokens);

    const roots = [
      ...new Set(editTokenIds(edit).flatMap((id) => rootOf.get(id) ?? [])),
    ];
    if (edit.op === "surface") {
      for (const root of roots) {
        const existing = lineages.get(root);
        const text = textOf(current.tokens, root);
        if (existing === undefined) {
          lineages.set(root, {
            root,
            surfacedAt: edit.at,
            attempts: 0,
            failedAttempts: 0,
            reSays: 0,
            outcome: "open",
            endedAt: null,
            pendingReplacementAt: null,
            heard: [text],
            finalText: null,
          });
        } else if (existing.pendingReplacementAt !== null) {
          existing.failedAttempts++;
          existing.pendingReplacementAt = null;
          existing.heard.push(text);
        }
      }
      continue;
    }

    const open = roots.flatMap((root) => {
      const lineage = lineages.get(root);
      return lineage?.outcome === "open" ? [lineage] : [];
    });
    if (edit.op === "delete") {
      if (open.some((l) => l.failedAttempts > 0)) {
        deletionsAfterFailedCorrection++;
      }
      for (const lineage of open) lineage.outcome = "deleted";
      continue;
    }
    // A replaced span becomes one word followed under the first surfaced
    // root it replaced; a span word nobody surfaced has nothing to follow.
    const replacement =
      edit.op === "re-say" && edit.result.outcome === "replaced"
        ? edit.result.tokens
        : null;
    const keeper = replacement === null ? undefined : open[0]?.root;
    if (keeper !== undefined) {
      for (const token of replacement ?? []) rootOf.set(token.id, keeper);
    }
    const merged =
      keeper === undefined ? open : open.filter((l) => l.root === keeper);
    for (const lineage of open) {
      if (!merged.includes(lineage)) lineages.delete(lineage.root);
    }
    for (const lineage of merged) {
      if (isChildAttempt(edit)) lineage.attempts++;
      if (isFailure(edit)) lineage.failedAttempts++;
      if (edit.op === "re-say") {
        lineage.reSays++;
        if (edit.result.outcome === "replaced") {
          lineage.pendingReplacementAt = edit.at;
        }
      }
      if (isResolving(edit)) {
        lineage.outcome = "resolved";
        lineage.endedAt = edit.at;
        lineage.finalText = resolvedText(edit) ?? textOf(before, lineage.root);
      }
      if (edit.op === "save-for-adult") {
        lineage.outcome = "saved-for-adult";
        lineage.endedAt = edit.at;
      }
    }
  }

  const words = [...lineages.values()].map((l): WordFrustration => {
    const settledByReplacement =
      l.outcome === "open" && l.pendingReplacementAt !== null;
    const outcome = settledByReplacement ? "resolved" : l.outcome;
    const endedAt = settledByReplacement ? l.pendingReplacementAt : l.endedAt;
    const finalText = settledByReplacement
      ? textOf(current.tokens, l.root)
      : l.finalText;
    const substituted =
      outcome === "resolved" &&
      l.failedAttempts > 0 &&
      finalText !== null &&
      l.heard.every(
        (h) => normalizedDistance(h, finalText) > SUBSTITUTION_DISTANCE,
      );
    return {
      root: l.root,
      attempts: l.attempts,
      failedAttempts: l.failedAttempts,
      reSays: l.reSays,
      outcome,
      msToResolution:
        endedAt === null || outcome === "deleted"
          ? null
          : endedAt - l.surfacedAt,
      substituted,
    };
  });

  const surfacedWords = words.length;
  const ratio = (n: number) => (surfacedWords === 0 ? null : n / surfacedWords);
  return {
    tag: "ok",
    value: {
      surfacedWords,
      words,
      meanAttempts: ratio(words.reduce((sum, w) => sum + w.attempts, 0)),
      medianMsToResolution: median(
        words.flatMap((w) =>
          w.msToResolution === null ? [] : [w.msToResolution],
        ),
      ),
      savedForAdultShare: ratio(
        words.filter((w) => w.outcome === "saved-for-adult").length,
      ),
      deletionsAfterFailedCorrection,
      substitutionsAfterFailedRecognition: words.filter((w) => w.substituted)
        .length,
    },
  };
}
